import { lazy, Suspense, useMemo } from 'react';
import { ThemeProvider, StyledEngineProvider, createTheme, GlobalStyles } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import { useSelector } from 'react-redux';
import { localeSelector } from 'reducers/userAuthSlice';
import { useLocation } from 'react-router-dom';
import 'dayjs/locale/it';
import { getTheme } from 'constants/styles/theme';
import { themeSelector } from 'reducers/globalSlice';

const CssBaseline = lazy(() => import('@mui/material/CssBaseline'));

const AppThemeProvider = ({ children }) => {
	const locale = useSelector(localeSelector);
	const selectedTheme = useSelector(themeSelector);
	const location = useLocation();

	const getMUILocaleFromAppLocale = appLocale => {
		switch (appLocale) {
			case 'it': {
				const { itIT } = import('@mui/material/locale');
				return itIT;
			}
			default: {
				const { enUS } = import('@mui/material/locale');
				return enUS;
			}
		}
	};

	// Determine theme based on route or explicit themeValue prop
	const shouldUseDarkTheme =
		selectedTheme === 'dark' ||
		location.pathname === '/login' ||
		location.pathname === '/signup' ||
		location.pathname === '/confirm-signup' ||
		location.pathname === '/reset-password' ||
		location.pathname.startsWith('/redeem/');

	const mode = shouldUseDarkTheme ? 'dark' : 'light';

	// Update the theme only if the mode changes (following MUI v5 dark mode docs pattern)
	const theme = useMemo(
		() => createTheme(getTheme(mode), getMUILocaleFromAppLocale(locale)),
		[mode, locale]
	);

	return (
		<LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={locale}>
			<StyledEngineProvider injectFirst>
				<ThemeProvider theme={theme}>
					<Suspense fallback={null}>
						<CssBaseline />
						<GlobalStyles
							styles={theme => {
								const isDark = theme.palette.mode === 'dark';

								const thumbColor = isDark
									? theme.palette.background.background2
									: theme.palette.grey[400];

								const thumbHoverColor = isDark
									? theme.palette.background.background2
									: theme.palette.grey[500];

								const trackColor = isDark
									? theme.palette.background.default
									: theme.palette.background.background2;

								const thumbBorderColor = isDark
									? `2px solid ${theme.palette.background.default}`
									: '2px solid transparent';

								return {
									a: {
										color: theme.palette.primary.main
									},
									'a:visited': {
										color: theme.palette.primary.main
									},
									'a:hover': {
										color: theme.palette.primary.light
									},
									'*': {
										scrollbarWidth: 'thin',
										scrollbarColor: `${thumbColor} ${trackColor}`
									},
									'*::-webkit-scrollbar': {
										width: '10px',
										height: '10px'
									},
									'*::-webkit-scrollbar-track': {
										backgroundColor: trackColor
									},
									'*::-webkit-scrollbar-thumb': {
										backgroundColor: thumbColor,
										borderRadius: '8px',
										border: thumbBorderColor,
										backgroundClip: 'padding-box'
									},
									'*::-webkit-scrollbar-thumb:hover': {
										backgroundColor: thumbHoverColor
									},

									// DataGrid toolbar menus/panels (density + columns) are rendered in a Popper and
									// do not use MuiMenu/MuiPopover, so style them explicitly for dark-mode contrast.
									'.MuiDataGrid-menu .MuiPaper-root, .MuiDataGrid-panel .MuiPaper-root': {
										backgroundColor: isDark
											? theme.palette.background.background1
											: theme.palette.background.default,
										color: theme.palette.text.primary,
										backgroundImage: 'none',
										boxShadow: isDark
											? `${theme.shadows[12]}, 0 0 0 1px ${theme.palette.divider}`
											: theme.shadows[8]
									}
								};
							}}
						/>
					</Suspense>
					{children}
				</ThemeProvider>
			</StyledEngineProvider>
		</LocalizationProvider>
	);
};
export default AppThemeProvider;
